import { useState, type InputHTMLAttributes, type ReactNode, useId } from "react";
import { Eye, EyeOff } from "lucide-react";
import { FieldWrapper } from "./FieldWrapper";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  prefixIcon?: ReactNode;
}

export default function Input({
  label,
  helper,
  error,
  required,
  prefixIcon,
  type = "text",
  className = "",
  id,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword && showPassword ? "text" : type;

  return (
    <FieldWrapper label={label} helper={helper} error={error} required={required} htmlFor={inputId}>
      <div className="relative">
        {prefixIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
            {prefixIcon}
          </span>
        )}
        <input
          id={inputId}
          type={resolvedType}
          className={`h-[42px] w-full rounded-lg border bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
            error
              ? "border-danger focus:ring-danger/30"
              : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
          } ${prefixIcon ? "pl-10" : "pl-3.5"} ${
            isPassword ? "pr-11" : "pr-3.5"
          } ${className}`}
          aria-invalid={!!error}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center text-ink-muted hover:text-ink cursor-pointer"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
          </button>
        )}
      </div>
    </FieldWrapper>
  );
}